import { useMemo, useState } from "react";

// Courbe d'historique de prix (SVG maison, aucune dépendance externe).
// Trace deux séries à partir des relevés quotidiens : Standard et Reverse.
// La métrique par défaut est la tendance (`trend`), avec repli sur la moyenne
// 1 jour (`avg1`) puis la moyenne globale (`avg`).
//
// L'historique se construit au fil du temps : tant qu'il n'y a pas au moins
// deux jours de relevés, on affiche un message plutôt qu'une courbe plate.

const W = 520;   // largeur logique du viewBox (responsive via width:100%)
const H = 160;
const PAD = { top: 12, right: 12, bottom: 22, left: 40 };

function pickStd(p) {
  return p.trend ?? p.avg1 ?? p.avg ?? null;
}
function pickRev(p) {
  return p.trend_holo ?? p.avg1_holo ?? null;
}

function fmtDate(d) {
  // "2026-07-21" -> "21/07"
  const [, m, day] = d.split("-");
  return `${day}/${m}`;
}

export const PriceHistoryChart = ({ points = [], secondaryLabel = "Reverse", secondaryColor = "#38bdf8" }) => {
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    const std = points.map((p, i) => ({ i, v: pickStd(p), date: p.date }));
    const rev = points.map((p, i) => ({ i, v: pickRev(p), date: p.date }));
    const allVals = [...std, ...rev].map(d => d.v).filter(v => v != null && !Number.isNaN(v));
    if (allVals.length === 0) return null;
    let min = Math.min(...allVals);
    let max = Math.max(...allVals);
    if (min === max) { min = Math.max(0, min - 0.1); max = max + 0.1; }
    const n = points.length;
    const x = (i) => PAD.left + (n <= 1 ? 0 : (i / (n - 1)) * (W - PAD.left - PAD.right));
    const y = (v) => PAD.top + (1 - (v - min) / (max - min)) * (H - PAD.top - PAD.bottom);
    const line = (arr) => arr
      .filter(d => d.v != null && !Number.isNaN(d.v))
      .map((d, k) => `${k === 0 ? "M" : "L"}${x(d.i).toFixed(1)},${y(d.v).toFixed(1)}`)
      .join(" ");
    return { min, max, n, x, y, stdPath: line(std), revPath: line(rev), std, rev };
  }, [points]);

  const distinctDays = new Set(points.map(p => p.date)).size;

  if (distinctDays < 2 || !model) {
    return (
      <div className="text-xs text-zinc-500 bg-zinc-800/40 rounded-lg p-3 text-center">
        Historique en cours de constitution — un relevé par jour à chaque ouverture
        de la fiche. Reviens dans quelques jours pour voir la courbe.
        {distinctDays === 1 && <span className="block mt-1 text-zinc-600">1 jour relevé pour l'instant.</span>}
      </div>
    );
  }

  const { min, max, x, y, stdPath, revPath, std, rev } = model;
  const hoverPoint = hover != null ? points[hover] : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-zinc-400">Historique (tendance)</span>
        <span className="flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1 text-emerald-400"><span className="inline-block w-3 h-0.5 bg-emerald-400" />Standard</span>
          <span className="flex items-center gap-1" style={{ color: secondaryColor }}>
            <span className="inline-block w-3 h-0.5" style={{ background: secondaryColor }} />{secondaryLabel}
          </span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto" }}
        onMouseLeave={() => setHover(null)}>
        {/* graduations Y : min / max */}
        <text x={PAD.left - 6} y={y(max) + 3} textAnchor="end" className="fill-zinc-500" fontSize="9">{max.toFixed(2)}€</text>
        <text x={PAD.left - 6} y={y(min) + 3} textAnchor="end" className="fill-zinc-500" fontSize="9">{min.toFixed(2)}€</text>
        <line x1={PAD.left} y1={y(max)} x2={W - PAD.right} y2={y(max)} stroke="currentColor" className="text-zinc-800" strokeWidth="1" />
        <line x1={PAD.left} y1={y(min)} x2={W - PAD.right} y2={y(min)} stroke="currentColor" className="text-zinc-800" strokeWidth="1" />
        {/* dates extrêmes */}
        <text x={PAD.left} y={H - 6} textAnchor="start" className="fill-zinc-500" fontSize="9">{fmtDate(points[0].date)}</text>
        <text x={W - PAD.right} y={H - 6} textAnchor="end" className="fill-zinc-500" fontSize="9">{fmtDate(points[points.length - 1].date)}</text>
        {/* courbes */}
        {revPath && <path d={revPath} fill="none" stroke={secondaryColor} strokeWidth="1.75" />}
        {stdPath && <path d={stdPath} fill="none" stroke="#34d399" strokeWidth="1.75" />}
        {/* survol : ligne verticale + points */}
        {hoverPoint && (
          <line x1={x(hover)} y1={PAD.top} x2={x(hover)} y2={H - PAD.bottom} stroke="currentColor" className="text-zinc-600" strokeWidth="1" strokeDasharray="3 3" />
        )}
        {hoverPoint && std[hover]?.v != null && <circle cx={x(hover)} cy={y(std[hover].v)} r="3" fill="#34d399" />}
        {hoverPoint && rev[hover]?.v != null && <circle cx={x(hover)} cy={y(rev[hover].v)} r="3" fill={secondaryColor} />}
        {/* zones de survol invisibles */}
        {points.map((p, i) => (
          <rect key={i} x={x(i) - (W / points.length) / 2} y={PAD.top}
            width={W / points.length} height={H - PAD.top - PAD.bottom}
            fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hoverPoint && (
        <div className="text-[11px] text-zinc-400 text-center -mt-1">
          {fmtDate(hoverPoint.date)} ·
          {std[hover]?.v != null && <span className="text-emerald-400"> Std {std[hover].v.toFixed(2)}€</span>}
          {rev[hover]?.v != null && <span style={{ color: secondaryColor }}> · {secondaryLabel} {rev[hover].v.toFixed(2)}€</span>}
        </div>
      )}
    </div>
  );
};
